import React, { useState, useEffect, useRef } from 'react';

function StatsSection() {
  const stats = [
    { number: "8.2", suffix: "k", label: "Garments Ordered" },
    { number: "10", suffix: "+", label: "Years of Expertise" },
    { number: "97", suffix: "%", label: "Happy Brides" },
  ];

  const [animatedValues, setAnimatedValues] = useState(stats.map(() => 0));
  const [hasAnimated, setHasAnimated] = useState(false);
  const sectionRef = useRef(null);

  const animateCounter = (targetValue, index, isDecimal = false) => {
    const target = parseFloat(targetValue);
    const duration = 1000;
    const steps = 60;
    const increment = target / steps;
    let current = 0;
    let step = 0;

    const timer = setInterval(() => {
      step++;
      current += increment;

      if (step >= steps) {
        current = target;
        clearInterval(timer);
      }

      setAnimatedValues((prev) => {
        const newValues = [...prev];
        newValues[index] = isDecimal
          ? parseFloat(current.toFixed(1))
          : Math.floor(current);
        return newValues;
      });
    }, duration / steps);

    return timer;
  };

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !hasAnimated) {
            setHasAnimated(true);
            stats.forEach((stat, index) => {
              const isDecimal = stat.number.includes('.');
              setTimeout(() => {
                animateCounter(stat.number, index, isDecimal);
              }, index * 200);
            });
          }
        });
      },
      {
        threshold: 0.3,
        rootMargin: '0px 0px -50px 0px',
      }
    );

    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => {
      if (sectionRef.current) observer.unobserve(sectionRef.current);
    };
  }, [hasAnimated]);

  const formatValue = (value, originalNumber) => {
    if (originalNumber.includes('.')) {
      return value.toFixed(1);
    }
    return Math.floor(value).toString();
  };

  return (
    <>
      {/* --- Small CSS targeting the 480px - 670px range --- */}
      <style>{`
        @media (min-width:480px) and (max-width:670px) {
          /* attach to the root section to scope styles */
          .stats-between-480-670 { padding-left: 12px; padding-right: 12px; }

          /* Make heading smaller and centered in this range */
          .stats-between-480-670 .stats-heading {
            font-size: 1.6rem; /* ~text-2xl */
            line-height: 1.05;
            text-align: center;
            margin-bottom: 6px;
          }

          /* Stats grid: two columns with balanced spacing */
          .stats-between-480-670 .stats-grid {
            gap: 10px;
            justify-content: center;
          }

          /* Each stat becomes ~45% width (two per row) */
          .stats-between-480-670 .stat-item {
            flex: 0 0 45%;
            max-width: 45%;
          }

          /* Make the last stat span full width (as you had previously) */
          .stats-between-480-670 .stat-item.last-full {
            flex: 0 0 100%;
            max-width: 100%;
          }

          /* Number sizing inside stats */
          .stats-between-480-670 .stat-number {
            font-size: 1.6rem; /* ~text-2xl */
          }

          /* Label sizing */
          .stats-between-480-670 .stat-label {
            font-size: 0.9rem; /* slightly smaller label */
          }
        }
      `}</style>

      <section
        ref={sectionRef}
        className="stats-between-480-670 flex flex-wrap gap-10 items-center justify-between self-center mt-10 w-full max-w-[90%] mx-auto
                   lg:flex-row lg:gap-10"
      >
        {/* Heading */}
        <div className="flex-1 text-5xl font-bold tracking-wider leading-tight text-zinc-950 stats-heading
                        lg:text-left md:text-4xl sm:text-3xl">
          <h2>
            BRIDAL BLISS IN <br className="hidden sm:inline" />
            FIGURES
          </h2>
        </div>

        {/* Stats Grid */}
        <div className="flex flex-wrap flex-1 gap-8 justify-center items-start max-w-[604px] w-full stats-grid">
          {stats.map((stat, index) => (
            <div
              key={index}
              className={`stat-item flex flex-col justify-center items-center text-center flex-1
                          ${index === 2 ? 'last-full' : ''}`}
            >
              <div className="stat-number text-5xl font-bold leading-none text-zinc-950 md:text-4xl sm:text-3xl">
                {formatValue(animatedValues[index], stat.number)}
                <span style={{ color: 'rgba(233,173,23,1)' }}>{stat.suffix}</span>
              </div>
              <p className="stat-label mt-2 text-base font-medium text-neutral-700 md:text-sm sm:text-sm">
                {stat.label}
              </p>
            </div>
          ))}
        </div>
      </section>
    </>
  );
}

export default StatsSection;
